import axios from "axios";

// API Base URLs
//
// Resolved from NEXT_PUBLIC_API_URL (set in .env.local or Railway environment).
// Falls back to the production backend so a missing env var fails safe (hits prod)
// rather than silently pointing the browser at localhost. For local dev, set
// NEXT_PUBLIC_API_URL in .env.local — or swap the fallback below for the local BE:
//   http://127.0.0.1:8000/api/v1
//
// The /api/v1 suffix is part of the base URL — all route helpers below append
// only the path segment (e.g. "/auth/login"). The shape-guard regex in
// __tests__/utils/api.test.ts asserts: /^https?:\/\/[^/]+\/api\/v\d+$/
export const API_URL =
  process.env.NEXT_PUBLIC_API_URL || "https://accesshunter-production-c961.up.railway.app/api/v1";
export const API_URL_WEB =
  process.env.NEXT_PUBLIC_API_URL || "https://accesshunter-production-c961.up.railway.app/api/v1";

// Axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Attach Bearer token automatically.
// Falls back to sessionStorage for users who logged in without "Remember Me".
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Normalize a Laravel `{ status, data }` success envelope down to its `data`
 * payload — the single fetch-boundary unwrap for non-paginated enveloped
 * endpoints (drivers this file, reusable by any future enveloped helper).
 *
 * It THROWS on any shape it doesn't recognize rather than returning a wrong
 * value, because a *silent* mismatch is exactly what caused T-249: the old
 * `return response.data` handed callers the whole envelope, `.forEach` threw,
 * and a bare `catch {}` swallowed it. With multiple response shapes live in
 * prod, a lenient `r => r.data.data` would silently misbehave — most
 * dangerously on **paginated** endpoints (`/properties`, `/ice_shacks`) which
 * return `{ data, links, meta }`: `.data.data` would appear to work while
 * dropping `links`/`meta`. So paginated bodies are rejected explicitly here;
 * they need a dedicated paginated unwrapper, not this one.
 *
 * This is intentionally NOT a global axios response interceptor — the backend
 * envelope is inconsistent per-endpoint (bare bodies, `{status,message}`,
 * `{status,data}`, and paginated `{data,links,meta}` all exist), so a single
 * app-wide unwrap is unsafe until the BE contract is standardized (T-262).
 *
 * @param {{ data?: unknown }} response - the raw axios response (only `.data` is read)
 * @returns {*} the envelope's `data` payload (array or object)
 * @throws {Error} if the body is not a plain `{ status, data }` envelope
 *   (missing `data`, a bare array/primitive, or a paginated `{data,links,meta}`)
 */
export const unwrapEnvelope = (response) => {
  const body = response?.data;

  if (body === null || typeof body !== "object" || Array.isArray(body)) {
    throw new Error(
      `unwrapEnvelope: expected a { status, data } envelope but received ${
        Array.isArray(body) ? "a bare array" : `a ${body === null ? "null" : typeof body}`
      }. This endpoint's response shape does not match the enveloped contract.`
    );
  }

  if ("links" in body || "meta" in body) {
    throw new Error(
      "unwrapEnvelope: received a paginated { data, links, meta } body. Use a " +
        "paginated unwrapper — unwrapping here would silently drop links/meta."
    );
  }

  if (!("data" in body)) {
    throw new Error(
      `unwrapEnvelope: envelope has no 'data' key (keys: ${
        Object.keys(body).join(", ") || "none"
      }).`
    );
  }

  return body.data;
};

/*
|--------------------------------------------------------------------------
| AUTH APIs (Client Contract)
|--------------------------------------------------------------------------
*/

// Register
export const registerUser = (data) => api.post("/auth/register", data);

// Login
export const loginUser = (data) => api.post("/auth/login", data);

// Logout
export const logoutUser = () => api.post("/auth/logout");

// Get current user
// Get current logged-in user
export const getMe = () => {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  return axios.get(`${API_URL}/me`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
};
// Password reset request
export const forgotPassword = (email) => api.post("/auth/password/reset-request", { email });

// Password reset
export const resetPassword = (data) => api.post("/auth/password/reset", data);

// T-183: the unified Google login helper was removed alongside the FE Google
// buttons after T-179 deleted the backend /auth/google route (returned 404).
// T-180 will re-add a server-verified Google flow.

// Update user profile
const getToken = () => localStorage.getItem("token") || sessionStorage.getItem("token");
export const updateUser = (payload) => {
  const token = getToken();
  return axios.put(`${API_URL}/update-user`, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

// Resend verification email
export const resendEmailVerification = () => {
  const token = getToken();
  return axios.post(
    `${API_URL}/email/resend`,
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
};

// Delete account
export const deleteAccount = () => {
  const token = getToken();
  return axios.delete(`${API_URL}/delete-account`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

/*
|--------------------------------------------------------------------------
| OPTIONAL (Legacy OTP - REMOVE in production)
|--------------------------------------------------------------------------
*/

// ❌ Not in client contract v1 (keep only if business requires OTP)
export const verifyOtp = (data) => api.post("/verify-otp", data);

export const resendOtp = (data) => api.post("/resend-otp", data);

//
//import axios from "axios";
//
////export const API_URL = "https://accesshunter-production.up.railway.app/api";
//export const API_URL = "http://127.0.0.1:8000/api";
//export const API_URL_WEB = "http://127.0.0.1:8000/api";
//
//
//
//export const verifyOtp = (data) => {
//  return axios.post(`${API_URL}/verify-otp`, data);
//};
//export const resendOtp = (data) => {
//  return axios.post(`${API_URL}/resend-otp`, data, {
//    headers: { "Content-Type": "application/json" }
//  });
//};
//
//export const registerUser = (data) =>
//  axios.post(`${API_URL}/register`, data);
//
//export const loginUser = (data) =>
//  axios.post(`${API_URL}/login`, data);
//
//export const forgotPassword = (email) =>
//  axios.post(`${API_URL}/forgot-password`, { email });
//
//export const resetPassword = (data) =>
//  axios.post(`${API_URL}/forgot-password/reset`, data);
//// --- new Google login function ---
//export const loginWithGoogle = async (payload) => {
//  console.log("🔹 Calling backend with Google payload:", payload);
//
//  try {
//    const response = await axios.post(`${API_URL}/auth/google-mobile`, payload, {
//      headers: { "Content-Type": "application/json" },
//    });
//
//    return response.data;
//  } catch (err) {
//    console.error("❌ Google login API error:", err);
//    throw err;
//  }
//};
//
/*
|--------------------------------------------------------------------------
| PROPERTY IMPORT API
|--------------------------------------------------------------------------
*/

export const importProperty = (data) => {
  return api.post("/import-property", data);
};

/*
|--------------------------------------------------------------------------
| BULK IMPORT (array of properties)
|--------------------------------------------------------------------------
*/

export const importProperties = async (properties) => {
  try {
    const results = [];
    for (const property of properties) {
      console.log("property ", property);
      const response = await api.post("/import-property", property);
      results.push(response.data);
    }

    return results;
  } catch (error) {
    console.error("❌ Import failed:", error);
    throw error;
  }
};

export const importIceShacks = async (ice_shacks) => {
  try {
    const results = [];

    for (const property of ice_shacks) {
      console.log("ice_shacks ", property);
      const response = await api.post("/import-ice_shacks", property);
      results.push(response.data);
    }

    console.log("✅ Properties imported:", results);

    return results;
  } catch (error) {
    console.error("❌ Import failed:", error);
    throw error;
  }
};

// Defensive unwrap for Laravel API Resource collections.
// BE T-031 (PR #14) migrated /properties + /my-properties from a bare array
// to the standard `{ data: [...], links?, meta? }` paginator envelope. This
// helper survives both shapes so the FE doesn't crash mid-deploy.
// Returns: the inner `data` array when an envelope is present, else the
// original payload (bare array or other shape).
export const unwrapResourceCollection = (payload) => {
  if (
    payload &&
    typeof payload === "object" &&
    !Array.isArray(payload) &&
    Array.isArray(payload.data)
  ) {
    return payload.data;
  }
  return payload;
};

// Sibling to unwrapResourceCollection — handles the single-resource envelope
// shape `{ data: { ... } }` (e.g., POST /properties returning the created
// resource via Resource::make()). Returns the inner object when an envelope
// is present, else the original payload.
export const unwrapResource = (payload) => {
  if (
    payload &&
    typeof payload === "object" &&
    payload.data &&
    typeof payload.data === "object" &&
    !Array.isArray(payload.data)
  ) {
    return payload.data;
  }
  return payload;
};

// Fetch all hunting properties with related data
export const getProperties = async () => {
  try {
    const response = await api.get("/properties");
    return unwrapResourceCollection(response.data);
  } catch (error) {
    console.error("❌ Failed to fetch properties:", error);
    throw error;
  }
};

// Fetch the canonical target-species vocabulary (T-084b).
//
// GET /species is PUBLIC (no auth) and rate-limited 60/min/IP. The response
// `data` is a GROUPED OBJECT — `{ "Big Game": [{value,label},...], ... }` —
// NOT a flat list and NOT a paginator. We therefore unwrap with
// `unwrapResource` (single-resource `{ data: {...} }` envelope), NOT
// `unwrapResourceCollection` (which expects `data` to be an array and would
// pass the object straight through unchanged). Group keys arrive pre-ordered
// (Big Game → Small Game → Birds → Predators); preserve insertion order.
export const getSpecies = async () => {
  const response = await api.get("/species");
  return unwrapResource(response.data); // → { "Big Game": [{value,label}], ... }
};

// Fetch the current user's hunting properties (landowner-scoped)
export const getMyProperties = async () => {
  try {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token) throw new Error("User not authenticated");

    const response = await api.get(`/my-properties`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return unwrapResourceCollection(response.data);
  } catch (error) {
    console.error("❌ Failed to check access request:", error.response?.data || error.message);
    throw error;
  }
};

export const get_ice_shacks = async () => {
  try {
    const response = await api.get("/ice_shacks");
    return unwrapResourceCollection(response.data);
  } catch (error) {
    console.error("❌ Failed to fetch properties:", error);
    throw error;
  }
};

export const get_my_ice_shacks = async () => {
  try {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token) throw new Error("User not authenticated");

    const response = await api.get(`/my_ice_shacks`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return unwrapResourceCollection(response.data);
  } catch (error) {
    console.error("❌ Failed to check access request:", error.response?.data || error.message);
    throw error;
  }
};

export const getLandownerRequest = async () => {
  try {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token) throw new Error("User not authenticated");

    const response = await api.get(`/access-requests/landowner/`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return unwrapResourceCollection(response.data);
  } catch (error) {
    console.error("❌ Failed to check access request:", error.response?.data || error.message);
    throw error;
  }
};

export const getAccessRequest = async () => {
  try {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token) throw new Error("User not authenticated");

    const response = await api.get(`/access-requests/user/`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return unwrapResourceCollection(response.data);
  } catch (error) {
    console.error("❌ Failed to check access request:", error.response?.data || error.message);
    throw error;
  }
};

export const checkAccessRequest = async (propertyId) => {
  try {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token) throw new Error("User not authenticated");

    const response = await api.get(`/access-requests/property/user/${propertyId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // T-233 (BE PR #88, contract settled on branch triage-rebook-non-overlapping-dates):
    // the endpoint now returns { success: true, data: [...] } — newest-first, ALL
    // statuses including draft — instead of a single object. unwrapResourceCollection
    // returns payload.data when it's an array (the extra `success` key is harmless).
    return unwrapResourceCollection(response.data);
  } catch (error) {
    console.error("❌ Failed to check access request:", error.response?.data || error.message);
    throw error;
  }
};

export const checkHunterAccessRequest = async (propertyId) => {
  try {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token) throw new Error("User not authenticated");

    const response = await api.get(`/access-requests/property/hunter/user/${propertyId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("❌ Failed to check access request:", error.response?.data || error.message);
    throw error;
  }
};

export const requestAccess = async (payload) => {
  try {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token) throw new Error("User is not authenticated");

    let response;

    // BE envelope is in flux (PR #49): id/client_secret may be top-level or nested
    // under access_request depending on the response shape. The `access_request`
    // fallback in the id logs below (both branches) can be removed once the contract
    // locks — see data/types.ts RequestAccessResponse and T-128.
    if (payload?.id) {
      // ✏️ UPDATE
      response = await api.put(`/access-requests/${encodeURIComponent(payload.id)}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (process.env.NODE_ENV !== "production") {
        console.log(
          "✏️ Access request updated: id=",
          response.data?.id ?? response.data?.access_request?.id
        );
      }
    } else {
      // ➕ CREATE
      response = await api.post("/access-requests", payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (process.env.NODE_ENV !== "production") {
        console.log(
          "✅ Access request created: id=",
          response.data?.id ?? response.data?.access_request?.id,
          "has_secret:",
          !!response.data?.client_secret
        );
      }
    }

    return response.data;
  } catch (error) {
    console.error("❌ Access request failed:", error.response?.data || error.message);
    throw error;
  }
};

export const iceFishingBooking = async (payload) => {
  console.log(payload);
  try {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token) throw new Error("User is not authenticated");

    let response;

    if (payload?.id) {
      // ✏️ UPDATE booking
      response = await api.put(`/ice-fishing-bookings/${payload.id}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } else {
      console.log("✅ Booking creating:");
      // ➕ CREATE booking
      response = await api.post("/ice-fishing-bookings", payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    }

    return response.data;
  } catch (error) {
    console.error("❌ Ice fishing booking failed:", error.response?.data || error.message);
    throw error;
  }
};

export const updateIceFishingBookingStatus = async (id, status, reason) => {
  try {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token) throw new Error("User is not authenticated");
    if (!id) throw new Error("Booking ID is required");

    // Optional free-text reason (mirrors updateHuntingStatus) — omit the key entirely
    // when not provided so the payload stays backward-compatible with the status-only
    // contract. Wire key is `note`: the BE validates/persists `note` (IceFishingBookingController
    // ::updateStatus `'note' => 'nullable|string'`, written to the status event and surfaced back
    // as `rejection_reason`). Sending `reason` here would be silently dropped (not in the allowlist).
    const payload = reason ? { status, note: reason } : { status };

    const response = await api.patch(
      `/ice-fishing-bookings/${encodeURIComponent(id)}/status`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("❌ Failed to update booking status:", error.response?.data || error.message);
    throw error;
  }
};

export const updateHuntingStatus = async (id, status, reason) => {
  try {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token) throw new Error("User is not authenticated");
    if (!id) throw new Error("Booking ID is required");

    // Optional free-text reason (FE-31) — omit the key entirely when not provided
    // so the payload stays backward-compatible with the status-only contract. Wire key is
    // `note`: the BE validates/persists `note` (AccessRequestController::updateStatus
    // `'note' => 'nullable|string|max:2000'`, written to the status event + rejection email,
    // surfaced back as `rejection_reason`). Sending `reason` would be silently dropped.
    const payload = reason ? { status, note: reason } : { status };

    const response = await api.patch(`/access-requests/${encodeURIComponent(id)}/status`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("❌ Failed to update booking status:", error.response?.data || error.message);
    throw error;
  }
};

// T-198: the hunter-writable cancel transition. Rewired off the PUT /access-requests/{id}
// (requestAccess) contract onto the dedicated BE endpoint (PR #79, merged to BE main) that
// runs refund logic server-side — mirrors updateHuntingStatus's shape exactly (token lookup,
// Authorization header, optional-note payload, rethrow raw so 404/409/422 bodies reach the
// caller unmodified for bookingActionErrorMessage to interpret).
export const cancelAccessRequest = async (id, note) => {
  try {
    // Auth via getToken() + the `api` instance's request interceptor (which attaches the
    // Bearer header for every api.* call) — do NOT hand-read the token or set a manual
    // header here; that bypasses both and the review gate blocks new bypasses. The guard
    // stays for a clean early error instead of a 401 round-trip.
    const token = getToken();
    if (!token) throw new Error("User is not authenticated");
    if (!id) throw new Error("Booking ID is required");

    // Optional free-text note — omit the key entirely when not provided. Wire key is
    // `note`, BE validates `'note' => 'nullable|string|max:2000'`.
    const payload = note ? { note } : {};

    const response = await api.post(`/access-requests/${encodeURIComponent(id)}/cancel`, payload);

    return response.data;
  } catch (error) {
    console.error("❌ Failed to cancel access request:", error.response?.data || error.message);
    throw error;
  }
};

// T-231: the ice-fishing member-writable cancel transition. Mirrors cancelAccessRequest's
// shape exactly (getToken guard, api-instance POST so the request interceptor attaches auth,
// optional-note payload, rethrow raw so the 404/422/503 bodies reach the caller unmodified
// for bookingActionErrorMessage to interpret). Deliberately NOT updateIceFishingBookingStatus
// (the host-only PATCH .../status, which 403s for a member caller) — this endpoint (BE branch)
// runs the member refund logic server-side.
export const iceFishingBookingCancel = async (id, note) => {
  try {
    // Auth via getToken() + the `api` instance's request interceptor (which attaches the
    // Bearer header for every api.* call) — do NOT hand-read the token or set a manual
    // header here; that bypasses both and the review gate blocks new bypasses. The guard
    // stays for a clean early error instead of a 401 round-trip.
    const token = getToken();
    if (!token) throw new Error("User is not authenticated");
    if (!id) throw new Error("Booking ID is required");

    // Optional free-text note — omit the key entirely when not provided. Wire key is
    // `note`, BE validates `'note' => 'nullable|string|max:2000'`.
    const payload = note ? { note } : {};

    const response = await api.post(
      `/ice-fishing-bookings/${encodeURIComponent(id)}/cancel`,
      payload
    );

    return response.data;
  } catch (error) {
    console.error(
      "❌ Failed to cancel ice fishing booking:",
      error.response?.data || error.message
    );
    throw error;
  }
};

// T-358 (SR-9): the post-transaction star-rating write. Mirrors cancelAccessRequest's
// shape exactly (getToken guard, api-instance POST so the request interceptor attaches
// auth, rethrow raw so the 4xx bodies — 409 duplicate / 422 ineligible / 403 forbidden —
// reach bookingActionErrorMessage/reviewActionErrorMessage unmodified). Stars only, v1:
// no `body` field. The BE endpoint is `POST /api/v1/reviews`; baseURL already carries the
// `/api/v1` prefix (see API_URL above), so the path segment here is `/reviews`.
//
// Payload contract (verified against merged BE T-335):
//   source_type: 'access_request' (hunt) | 'ice_fishing_booking' (ice)
//   source_id:   the transaction uuid (booking.id — a string uuid, NOT member_user_id)
//   target_type: 'hunting_property' | 'ice_shack' | 'landowner' | 'host' | 'member'
//   rating:      integer 1–5
export const createReview = async ({ source_type, source_id, target_type, rating }) => {
  try {
    // Auth via getToken() + the `api` instance's request interceptor (which attaches the
    // Bearer header for every api.* call) — do NOT hand-read the token or set a manual
    // header here; that bypasses both and the review gate blocks new bypasses. The guard
    // stays for a clean early error instead of a 401 round-trip.
    const token = getToken();
    if (!token) throw new Error("User is not authenticated");
    if (!source_id) throw new Error("Booking ID is required");
    if (!Number.isInteger(rating) || rating < 1 || rating > 5) {
      throw new Error("Rating must be an integer from 1 to 5");
    }

    const response = await api.post("/reviews", {
      source_type,
      source_id,
      target_type,
      rating,
    });

    return response.data;
  } catch (error) {
    console.error("❌ Failed to submit review:", error.response?.data || error.message);
    throw error;
  }
};

export const checkHunterBooking = async (propertyId) => {
  try {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token) throw new Error("User not authenticated");

    const response = await api.get(`/ice-fishing-bookings/property/hunter/${propertyId}/me`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("❌ Failed to check access request:", error.response?.data || error.message);
    throw error;
  }
};

// T-233 — NEW endpoint (BE PR #88): returns the hunter's ice-fishing booking
// REQUESTS for this ice shack as a collection { success: true, data: [...] },
// newest-first, ALL statuses — for the date-aware re-book gate on
// app/ice/booking/BookingCard.tsx. Distinct from checkHunterBooking above,
// which hits the UNCHANGED `.../hunter/{id}/me` single-object detail path
// consumed by the status/summary pages — do not conflate the two.
export const checkHunterBookingRequests = async (iceShackId) => {
  try {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token) throw new Error("User not authenticated");

    const response = await api.get(`/ice-fishing-bookings/property/hunter/${iceShackId}/requests`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return unwrapResourceCollection(response.data);
  } catch (error) {
    console.error(
      "❌ Failed to check ice-fishing booking requests:",
      error.response?.data || error.message
    );
    throw error;
  }
};

export const checkBooking = async (propertyId) => {
  try {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token) throw new Error("User not authenticated");

    const response = await api.get(`/ice-fishing-bookings/property/${propertyId}/me`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("❌ Failed to check access request:", error.response?.data || error.message);
    throw error;
  }
};

// T-122 — Availability lookups (blocked date ranges, per quarter/hut).
//
// PUBLIC reads, scoped to `published` listings (unpublished → 404). Unlike
// checkAccessRequest/checkHunterBooking above, these do NOT require a token —
// the shared `api` instance's request interceptor attaches Bearer if one
// happens to be present, but an anonymous/unauthenticated caller can still
// hit these endpoints. Bare paths — `API_URL` already includes `/api/v1`.
// Dynamic id segment wrapped in encodeURIComponent, matching every other
// path interpolation in this file (see getLegalDocument above).
export const getPropertyAvailability = async (propertyId) => {
  try {
    const response = await api.get(`/properties/${encodeURIComponent(propertyId)}/availability`);
    return response.data;
  } catch (error) {
    console.error(
      "❌ Failed to fetch property availability:",
      error.response?.data || error.message
    );
    throw error;
  }
};

export const getIceShackAvailability = async (iceShackId) => {
  try {
    const response = await api.get(`/ice_shacks/${encodeURIComponent(iceShackId)}/availability`);
    return response.data;
  } catch (error) {
    console.error(
      "❌ Failed to fetch ice shack availability:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/*
|--------------------------------------------------------------------------
| VEHICLE INFO (T-215)
|--------------------------------------------------------------------------
*/

// Fetch the hunter's most recently used complete vehicle info (make + model + license
// plate) so a booking-capture form can prefill VehicleInfoCard instead of asking a
// returning hunter to re-type it. All-null fields mean the hunter has no prior complete
// vehicle set (a normal response shape, not an error).
export const getLastVehicle = async () => {
  try {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token) throw new Error("User not authenticated");

    const response = await api.get(`/bookings/last-vehicle`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.error("❌ Failed to fetch last vehicle:", error.response?.data || error.message);
    }
    throw error;
  }
};

/*
|--------------------------------------------------------------------------
| SUBSCRIPTIONS (T-013 / WS-2a)
|--------------------------------------------------------------------------
*/

// Create a Pro annual subscription. FE supplies `early_signup` (honor-system).
// Returns CreateSubscriptionResponse from backend (subscription_id, status,
// renewal_date, client_secret). client_secret is null for $0-invoice cases
// (e.g. 100%-off coupon); caller must defend that branch.
export const createSubscription = async ({ early_signup }) => {
  try {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token) throw new Error("User is not authenticated");

    const response = await api.post(
      "/subscriptions",
      { early_signup: Boolean(early_signup) },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("❌ Failed to create subscription:", error.response?.data || error.message);
    throw error;
  }
};

// Fetch the current user's membership state. Returns MeResponse | null.
// Treats 404 as null (no subscription yet) so the dashboard can render the
// free-tier upsell without throwing.
export const getMySubscription = async () => {
  try {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token) throw new Error("User is not authenticated");

    const response = await api.get("/subscriptions/me", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    console.error("❌ Failed to fetch subscription:", error.response?.data || error.message);
    throw error;
  }
};

// Cancel a Pro subscription at period end. Backend keys on the Stripe
// subscription id (e.g. "sub_xxx"), not the local row id.
export const cancelSubscription = async (stripeSubscriptionId) => {
  try {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token) throw new Error("User is not authenticated");
    if (!stripeSubscriptionId) throw new Error("Stripe subscription ID is required");

    const response = await api.delete(`/subscriptions/${stripeSubscriptionId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("❌ Failed to cancel subscription:", error.response?.data || error.message);
    throw error;
  }
};

/*
|--------------------------------------------------------------------------
| NO-HUNTING SUBSCRIPTION (T-211 / BE PR #83)
|--------------------------------------------------------------------------
| Property-scoped $20/yr entitlement that marks a hunting property as a "No
| Hunting" sign. Mirrors the SUBSCRIPTIONS block above: the 201 body carries
| client_secret (nullable) for confirmPayment; activation is webhook-driven
| so callers must poll GET for status:"active" rather than trusting the 201.
| 409 means a live entitlement already exists — do not re-charge.
*/

export const createNoHuntingSubscription = async (propertyId) => {
  try {
    const response = await api.post(
      `/properties/${encodeURIComponent(propertyId)}/no-hunting-subscription`
    );
    return response.data;
  } catch (error) {
    console.error(
      "❌ Failed to create no-hunting subscription:",
      error.response?.data || error.message
    );
    throw error;
  }
};

export const getNoHuntingSubscription = async (propertyId) => {
  try {
    const response = await api.get(
      `/properties/${encodeURIComponent(propertyId)}/no-hunting-subscription`
    );
    return response.data;
  } catch (error) {
    console.error(
      "❌ Failed to fetch no-hunting subscription:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/*
|--------------------------------------------------------------------------
| LEGAL APIs (Public — no auth header)
|--------------------------------------------------------------------------
| Uses bare axios, NOT the shared `api` instance. The `api` instance has a
| request interceptor (~line 25) that auto-attaches `Authorization: Bearer
| ${token}` from localStorage. This endpoint is public; the user's token
| must NOT leak. Do not migrate this call to the `api` instance.
*/
export const getLegalDocument = (slug) => axios.get(`${API_URL}/legal/${encodeURIComponent(slug)}`);

/*
|--------------------------------------------------------------------------
| LANDOWNER CRUD — Properties (T-026 / WS-2b)
|--------------------------------------------------------------------------
*/

export const createProperty = async (payload) => {
  const response = await api.post("/landowner/properties", payload);
  return response.data;
};

export const updateProperty = async (id, payload) => {
  const response = await api.put(`/landowner/properties/${encodeURIComponent(id)}`, payload);
  return response.data;
};

export const deleteProperty = async (id) => {
  const response = await api.delete(`/landowner/properties/${encodeURIComponent(id)}`);
  return response.data;
};

// T-211 — owner-initiated reversal of a No Hunting listing. Clears the
// `no_hunting` marker; the underlying $20/yr entitlement is left untouched
// (no refund). Re-marking a listing No Hunting after this is creation-only on
// the BE (POST /properties/{id}/no-hunting-subscription 409s on a re-list).
export const flipPropertyToNormal = async (id) => {
  const response = await api.post(`/landowner/properties/${encodeURIComponent(id)}/flip-to-normal`);
  return response.data;
};

/*
|--------------------------------------------------------------------------
| LANDOWNER CRUD — Ice Shacks (T-026 / WS-2b)
|--------------------------------------------------------------------------
| BE exposes ice-shack writes via Route::apiResource('ice-shacks', ...) — kebab in URL.
| Snake_case 'ice_shacks' is retained for the public read endpoint and DB model name.
*/

export const createIceShack = async (payload) => {
  const response = await api.post("/landowner/ice-shacks", payload);
  return response.data;
};

export const updateIceShack = async (id, payload) => {
  const response = await api.put(`/landowner/ice-shacks/${encodeURIComponent(id)}`, payload);
  return response.data;
};

export const deleteIceShack = async (id) => {
  const response = await api.delete(`/landowner/ice-shacks/${encodeURIComponent(id)}`);
  return response.data;
};

/*
|--------------------------------------------------------------------------
| LANDOWNER CRUD — Quarters (T-026 / WS-2b)
|--------------------------------------------------------------------------
*/

// Reads stay un-prefixed: GET /properties/{id}/quarters is a public route outside the
// landowner middleware group (BE routes/api.php:94). Writes below require /landowner/ prefix.
export const getQuarters = async (propertyId) => {
  const response = await api.get(`/properties/${encodeURIComponent(propertyId)}/quarters`);
  return unwrapResourceCollection(response.data);
};

export const createQuarter = async (propertyId, payload) => {
  const response = await api.post(
    `/landowner/properties/${encodeURIComponent(propertyId)}/quarters`,
    payload
  );
  return response.data;
};

export const updateQuarter = async (propertyId, qid, payload) => {
  const response = await api.put(
    `/landowner/properties/${encodeURIComponent(propertyId)}/quarters/${encodeURIComponent(qid)}`,
    payload
  );
  return response.data;
};

export const deleteQuarter = async (propertyId, qid) => {
  const response = await api.delete(
    `/landowner/properties/${encodeURIComponent(propertyId)}/quarters/${encodeURIComponent(qid)}`
  );
  return response.data;
};

/*
|--------------------------------------------------------------------------
| LANDOWNER — Listing Images (T-127)
|--------------------------------------------------------------------------
| Multi-image upload / delete / reorder for hunting properties and ice shacks
| (BE T-126). Upload uses multipart/form-data with the `images[]` array field;
| per D8 the `Content-Type` header is set to `undefined` so axios infers the
| multipart boundary from the FormData instance (the JSON singleton default is
| NOT mutated). Reorder sends the COMPLETE ordered id set under the `order` key
| — the BE rejects partial/foreign/missing id sets. All path params are
| encodeURIComponent-wrapped (defense-in-depth even for server-issued ids).
|
| PhotoUploadGrid calls the upload helpers with a single-file array per request
| so each tile has an independent status / retry lifecycle.
*/

/**
 * Upload one or more images to a hunting property.
 * @param {string} propertyId
 * @param {File[]} files - one or more image files (jpg/jpeg/png/webp, max 5 MB each)
 * @returns {Promise<{images: Array<{id: string, file_url: string, sort_order: number}>}>}
 */
export const uploadPropertyImages = async (propertyId, files) => {
  const formData = new FormData();
  files.forEach((f) => formData.append("images[]", f));

  const response = await api.post(
    `/landowner/properties/${encodeURIComponent(propertyId)}/images`,
    formData,
    { headers: { "Content-Type": undefined } }
  );
  return response.data;
};

/**
 * Delete a single image from a hunting property.
 * @param {string} propertyId
 * @param {string} imageId
 */
export const deletePropertyImage = async (propertyId, imageId) => {
  const response = await api.delete(
    `/landowner/properties/${encodeURIComponent(propertyId)}/images/${encodeURIComponent(imageId)}`
  );
  return response.data;
};

/**
 * Reorder a hunting property's images.
 * @param {string} propertyId
 * @param {string[]} orderedIds - the COMPLETE current ordered id set
 */
export const reorderPropertyImages = async (propertyId, orderedIds) => {
  const response = await api.patch(
    `/landowner/properties/${encodeURIComponent(propertyId)}/images/reorder`,
    { order: orderedIds }
  );
  return response.data;
};

/**
 * Upload one or more images to an ice shack.
 * @param {string} shackId
 * @param {File[]} files - one or more image files (jpg/jpeg/png/webp, max 5 MB each)
 */
export const uploadIceShackImages = async (shackId, files) => {
  const formData = new FormData();
  files.forEach((f) => formData.append("images[]", f));

  const response = await api.post(
    `/landowner/ice-shacks/${encodeURIComponent(shackId)}/images`,
    formData,
    { headers: { "Content-Type": undefined } }
  );
  return response.data;
};

/**
 * Delete a single image from an ice shack.
 * @param {string} shackId
 * @param {string} imageId
 */
export const deleteIceShackImage = async (shackId, imageId) => {
  const response = await api.delete(
    `/landowner/ice-shacks/${encodeURIComponent(shackId)}/images/${encodeURIComponent(imageId)}`
  );
  return response.data;
};

/**
 * Reorder an ice shack's images.
 * @param {string} shackId
 * @param {string[]} orderedIds - the COMPLETE current ordered id set
 */
export const reorderIceShackImages = async (shackId, orderedIds) => {
  const response = await api.patch(
    `/landowner/ice-shacks/${encodeURIComponent(shackId)}/images/reorder`,
    { order: orderedIds }
  );
  return response.data;
};

/*
|--------------------------------------------------------------------------
| LANDOWNER — Document Upload + Application Submission (T-020 / FE-26)
|--------------------------------------------------------------------------
*/

/**
 * Upload or replace a landowner identity document.
 *
 * Uses multipart/form-data. Per D8: `Content-Type` is set to `undefined`
 * so axios infers the correct `multipart/form-data; boundary=…` header from
 * the FormData instance. The singleton default (`application/json`) is NOT
 * mutated.
 *
 * @param {File} file - The document file (PDF, JPEG, or PNG, max 10 MB).
 * @param {string} documentType - One of: drivers_license | land_title | utility_bill
 * @returns {Promise<{id: number, document_type: string, original_name: string, signed_url: string}>}
 */
export const uploadLandownerDocument = async (file, documentType) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("document_type", documentType);

  const response = await api.post("/landowner/documents", formData, {
    headers: { "Content-Type": undefined },
  });
  return unwrapEnvelope(response);
};

/**
 * List all documents uploaded by the current landowner.
 *
 * @returns {Promise<Array<{id: number, document_type: string, original_name: string, signed_url: string}>>}
 */
export const listLandownerDocuments = async () => {
  const response = await api.get("/landowner/documents");
  return unwrapEnvelope(response);
};

/**
 * Submit landowner application for admin review.
 * @returns {Promise<{message: string}>} on 200 success
 * @throws axios error on 422 — err.response.data shape:
 *   { message: string, missing_documents: ("drivers_license" | "land_title" | "utility_bill")[] }
 *   missing_documents is ordered per BE LandownerDocumentAttributes::requiredTypes().
 *   Endpoint: POST /api/v1/landowner/submit-for-approval
 */
export const submitLandownerForApproval = async () => {
  const response = await api.post("/landowner/submit-for-approval");
  return response.data;
};

/*
|--------------------------------------------------------------------------
| ADMIN — Landowner Approval APIs (T-021 / FE-52)
|--------------------------------------------------------------------------
| All four helpers use the same axios singleton + Bearer interceptor above.
| Routes are guarded server-side by auth:sanctum + role:admin middleware
| (BE PR #17 / LandownerApprovalController).
*/

/**
 * List all landowners with status=pending awaiting admin review.
 *
 * @returns {Promise<Array<{
 *   id: number,
 *   uuid: string,
 *   name: string,
 *   email: string,
 *   created_at: string,
 *   documents: Array<{id: number, document_type: string, original_name: string, signed_url: string}>
 * }>>}
 */
export const getPendingLandowners = async () => {
  const response = await api.get("/admin/landowners/pending");
  return response.data;
};

/**
 * Fetch a single pending landowner with their documents for review.
 *
 * @param {string|number} id - The landowner user UUID or ID.
 * @returns {Promise<{
 *   id: number,
 *   uuid: string,
 *   name: string,
 *   email: string,
 *   created_at: string,
 *   documents: Array<{id: number, document_type: string, original_name: string, signed_url: string}>
 * }>}
 */
export const getLandownerForReview = async (id) => {
  const response = await api.get(`/admin/landowners/${encodeURIComponent(id)}`);
  return response.data;
};

/**
 * Approve a landowner application — sets user.status to "active".
 *
 * @param {string|number} id - The landowner user UUID or ID.
 * @returns {Promise<{message: string}>}
 * @throws axios 422 if user is not currently pending.
 */
export const approveLandowner = async (id) => {
  const response = await api.post(`/admin/landowners/${encodeURIComponent(id)}/approve`);
  return response.data;
};

/**
 * Reject a landowner application — sets user.status to "rejected" and
 * persists the optional reason.
 *
 * @param {string|number} id - The landowner user UUID or ID.
 * @param {string|undefined} reason - Optional rejection reason (max 1000 chars).
 * @returns {Promise<{message: string}>}
 * @throws axios 422 if user is not currently pending.
 */
export const rejectLandowner = async (id, reason) => {
  const body = reason !== undefined && reason !== "" ? { reason } : {};
  const response = await api.post(`/admin/landowners/${encodeURIComponent(id)}/reject`, body);
  return response.data;
};

// ============================================================================
// ADMIN — Listing Approval APIs (T-022 / FE-53)
// ============================================================================
//
// Backend contract (PR #18 in accesshunter-back, merged):
//   GET  /admin/listings/{type}/pending           paginate(20), pending_approval only
//   GET  /admin/listings/{type}/{id}              single listing (any status, supports re-review)
//   POST /admin/listings/{type}/{id}/{action}     approve | reject | hide | suspend | remove
//
// `type` segment is HYPHENATED: "hunting" | "ice-shack". Keep the union narrow
// at call sites — passing arbitrary strings will route to non-existent routes.
//
// Allowed-from state machine (from BE controllers, mirror in UI gating):
//   approve  ← pending_approval | hidden | suspended  → published
//   reject   ← pending_approval                       → rejected
//   hide     ← published                              → hidden
//   suspend  ← published                              → suspended
//   remove   ← any                                    → removed

/**
 * Fetch the pending-approval queue for a single listing type.
 *
 * All dynamic URL segments are encodeURIComponent-wrapped as defense-in-depth
 * against caller drift — the helper self-defends, callers don't need to.
 *
 * @param {"hunting" | "ice-shack"} type
 * @returns {Promise<unknown>} Laravel paginator envelope ({data:[...], meta, links})
 *                             or bare array depending on BE serialization.
 */
export const getPendingAdminListings = async (type) => {
  const response = await api.get(`/admin/listings/${encodeURIComponent(type)}/pending`);
  return response.data;
};

/**
 * Fetch a single listing for admin review (any status — supports re-review).
 *
 * @param {"hunting" | "ice-shack"} type
 * @param {string} id
 * @returns {Promise<unknown>}
 */
export const getAdminListing = async (type, id) => {
  const response = await api.get(
    `/admin/listings/${encodeURIComponent(type)}/${encodeURIComponent(id)}`
  );
  return response.data;
};

/**
 * Perform an admin action on a listing (approve | reject | hide | suspend | remove).
 *
 * @param {"hunting" | "ice-shack"} type
 * @param {string} id
 * @param {"approve" | "reject" | "hide" | "suspend" | "remove"} action
 * @param {object} [body] - Optional body (e.g. {reason} for reject).
 * @returns {Promise<unknown>}
 * @throws axios 422 if listing's status isn't in the allowed-from set for this action.
 */
export const adminListingAction = async (type, id, action, body = {}) => {
  const response = await api.post(
    `/admin/listings/${encodeURIComponent(type)}/${encodeURIComponent(id)}/${encodeURIComponent(action)}`,
    body
  );
  return response.data;
};

/**
 * Fetch admin dashboard stats (T-330 / T-329).
 *
 * Shape aligned to backend PR #120 (GET /api/v1/admin/stats): segment counts are
 * NESTED under `counts`, and `trend` is a sibling array of per-(date, role) rows.
 * PR #120 is merged; callers must still handle the request failing (a 404 before
 * the backend deploys to staging, or a 500) via `asAdminApiError` (see
 * app/admin/stats/page.tsx).
 *
 * @returns {Promise<{
 *   counts: {
 *     total_registered: number,
 *     hunters: number,
 *     landowners: { total: number, pending: number },
 *     paid: number,
 *     operators: number
 *   },
 *   trend: Array<{ date: string, role: string, count: number }>
 * }>}
 */
export const getAdminStats = async () => {
  const response = await api.get("/admin/stats");
  return response.data;
};

/*
|--------------------------------------------------------------------------
| MESSAGING APIs (T-037 / WS-2)
|--------------------------------------------------------------------------
| All four helpers follow the frozen API contract (§7 of joint plan 2026-05-11):
|   GET  /messages/threads               → { data: [ThreadSummary...] }
|   GET  /messages/threads/{id}          → { data: [Message...], meta }
|   POST /messages/threads/{id}          body: { body: string } → 201 { data: Message }
|   POST /messages/threads/{id}/read     → 204
|
| All paths go through the `api` singleton (Bearer interceptor auto-attaches).
| Non-participant → 404 (per contract); 403 is not used.
*/

/**
 * List all message threads for the current user, ordered by last_message_at desc.
 * @returns {Promise<{data: Array<{id, subject_type, subject_id, last_message_at, unread_count, peer: {id, name}, preview}>}>}
 */
export const getThreads = () => api.get("/messages/threads").then((r) => r.data);

/**
 * Fetch paginated messages for a single thread.
 * @param {string} id - Thread UUID
 * @returns {Promise<{data: Array<{id, author_user_id, author_kind, body, created_at, read_at}>, meta}>}
 */
export const getThread = (id, page = undefined) => {
  const url = `/messages/threads/${encodeURIComponent(id)}` + (page ? `?page=${page}` : "");
  return api.get(url).then((r) => r.data);
};

/**
 * Post a new human message into a thread.
 * @param {string} id - Thread UUID
 * @param {string} body - Message text (max 5000 chars)
 * @returns {Promise<{data: {id, author_user_id, author_kind, body, created_at, read_at}}>}
 */
export const postMessage = (id, body) =>
  api.post(`/messages/threads/${encodeURIComponent(id)}`, { body }).then((r) => r.data);

/**
 * Mark all unread messages in a thread as read for the current user.
 * @param {string} id - Thread UUID
 * @returns {Promise<void>} — BE returns 204 No Content
 */
export const markThreadRead = (id) =>
  api.post(`/messages/threads/${encodeURIComponent(id)}/read`).then((r) => r.data);

/*
|--------------------------------------------------------------------------
| PAYOUTS (T-038 / Stripe Connect)
|--------------------------------------------------------------------------
| All three helpers use the `api` singleton (Bearer interceptor auto-attaches).
| Routes are guarded server-side by auth:sanctum + role:landowner middleware.
|
|   GET  /landowner/payout-account          → { payout_account: null | {...} }
|   POST /landowner/payout-account          → 201 { account_link_url }
|                                             409 if account already exists (let callers handle)
|   POST /landowner/payout-account/refresh  → 200 { account_link_url }
*/

/**
 * Fetch the current landowner's Stripe Connect payout account status.
 * Returns { payout_account: null } when no account has been created yet.
 *
 * @returns {Promise<{ payout_account: import('../types/payout').PayoutAccount | null }>}
 */
export const getPayoutAccount = async () => {
  try {
    const response = await api.get("/landowner/payout-account");
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch payout account:", error.response?.data);
    throw error;
  }
};

/**
 * Create a Stripe Connect account for the current landowner and return a
 * single-use onboarding link. Throws on 409 (account already exists) — caller
 * must handle that branch (usually by calling refreshPayoutLink instead).
 *
 * @returns {Promise<{ account_link_url: string }>}
 */
export const createPayoutAccount = async () => {
  try {
    const response = await api.post("/landowner/payout-account");
    return response.data;
  } catch (error) {
    console.error("❌ Failed to create payout account:", error.response?.data);
    throw error;
  }
};

/**
 * Refresh the Stripe Connect onboarding link when the previous single-use
 * link has expired. Returns a new link URL.
 *
 * @returns {Promise<{ account_link_url: string }>}
 */
export const refreshPayoutLink = async () => {
  try {
    const response = await api.post("/landowner/payout-account/refresh");
    return response.data;
  } catch (error) {
    console.error("❌ Failed to refresh payout link:", error.response?.data);
    throw error;
  }
};
